import React from "react";
import styled from "styled-components";
import { style } from "../style";
import { isObject } from "../utils/isObject";
import { ItemizedRow } from "./ItemizedRow";
import { ItemizedSpacer } from "./ItemizedSpacer";
import type { ItemizedSummaryLineItem, ItemizedSummaryLineItemDetails, ItemizedSummaryLineItemSubtitle } from "./types";

const InnerContainer = styled.div<{ maxHeight?: number }>`
  all: unset;
  box-sizing: border-box;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow-y: scroll;

  ${(props) => {
    if (props.maxHeight !== undefined) {
      return {
        maxHeight: props.maxHeight,
      };
    }
  }};
`;

const LineItemDetailsName = styled.span`
  all: unset;
  display: inline;
  font-weight: ${style.vars.fontWeight.bold};
`;

const LineItemDetails: React.FC<ItemizedSummaryLineItemDetails> = (props) => {
  return (
    <div>
      <LineItemDetailsName>{props.name}:</LineItemDetailsName> <span>{props.description}</span>
    </div>
  );
};

const normalizeSubtitle = (subtitle?: ItemizedSummaryLineItemSubtitle): React.ReactNode => {
  if (!subtitle || typeof subtitle === "string" || (isObject(subtitle) && React.isValidElement(subtitle))) {
    return subtitle;
  }

  if (Array.isArray(subtitle)) {
    return (
      <>
        {subtitle.map((details) => (
          <LineItemDetails key={details.name} {...details} />
        ))}
      </>
    );
  }

  return null;
};

type Props = {
  items: ItemizedSummaryLineItem[];
  UNSAFE_maxHeight?: number;
};

export const ItemizedList: React.FC<Props> = (props) => {
  const { items, UNSAFE_maxHeight } = props;

  return (
    <InnerContainer maxHeight={UNSAFE_maxHeight}>
      {items.map((item, index) => {
        const subtitle = normalizeSubtitle(item.subtitle);

        return (
          <React.Fragment key={index}>
            <ItemizedRow title={item.title} subtitle={subtitle} icon={item.icon} total={item.total} notes={item.notes} />
            {index < items.length - 1 && <ItemizedSpacer $dashed />}
          </React.Fragment>
        );
      })}
    </InnerContainer>
  );
};

ItemizedList.displayName = "ItemizedList";
